import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const CartContext = createContext(null);
const LS_KEY = "ssr_cart_v1";

export function CartProvider({ children }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);

  // Load on mount and when user changes
  useEffect(() => {
    async function load() {
      if (user) {
        // Merge guest items if any
        const guest = JSON.parse(localStorage.getItem(LS_KEY) || "[]");
        if (guest.length > 0) {
          try {
            const { data } = await api.post("/cart/merge", { items: guest });
            setItems(data.items);
            localStorage.removeItem(LS_KEY);
            return;
          } catch { /* fallthrough */ }
        }
        try {
          const { data } = await api.get("/cart");
          setItems(data.items);
        } catch { setItems([]); }
      } else {
        setItems(JSON.parse(localStorage.getItem(LS_KEY) || "[]"));
      }
    }
    load();
  }, [user]);

  const persistGuest = (next) => localStorage.setItem(LS_KEY, JSON.stringify(next));

  const add = useCallback(async (product, quantity = 1) => {
    const pid = product.id;
    if (user) {
      const { data } = await api.post("/cart", { product_id: pid, quantity });
      setItems(data.items);
    } else {
      const next = [...items];
      const idx = next.findIndex((x) => x.product_id === pid);
      if (idx >= 0) next[idx].quantity += quantity;
      else next.push({ product_id: pid, quantity });
      setItems(next); persistGuest(next);
    }
    toast.success("Added to cart", { description: product.name });
  }, [items, user]);

  const setQty = useCallback(async (product_id, quantity) => {
    if (user) {
      const { data } = await api.put(`/cart/${product_id}`, { product_id, quantity });
      setItems(data.items);
    } else {
      let next = items.map((x) => (x.product_id === product_id ? { ...x, quantity } : x));
      if (quantity <= 0) next = next.filter((x) => x.product_id !== product_id);
      setItems(next); persistGuest(next);
    }
  }, [items, user]);

  const remove = useCallback(async (product_id) => {
    if (user) {
      const { data } = await api.delete(`/cart/${product_id}`);
      setItems(data.items);
    } else {
      const next = items.filter((x) => x.product_id !== product_id);
      setItems(next); persistGuest(next);
    }
  }, [items, user]);

  const clear = useCallback(async () => {
    if (user) {
      for (const it of items) {
        await api.delete(`/cart/${it.product_id}`);
      }
      setItems([]);
    } else {
      setItems([]); persistGuest([]);
    }
  }, [items, user]);

  const count = items.reduce((s, i) => s + i.quantity, 0);

  return (
    <CartContext.Provider value={{ items, add, setQty, remove, clear, count }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
